import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { X, Play, SkipForward, Home, Sparkles, Volume2, VolumeX } from 'lucide-react';

// Aria's Guide Bot - Flying Robot Tour Guide

// Operator · Federation Tour Guide — Aria's tour guide / system assistant / security persona
// SFUI-004 Rev 3: uses the canonical bot base in BLUE spectral class (Tour/Operator).
import { BOT_BASE_URL } from '../constants/federationFaces';
const OPERATOR_AVATAR_URL = BOT_BASE_URL;
const OPERATOR_LABEL = 'Operator · Federation Tour Guide';
const OPERATOR_TAGLINE = 'Tour guide · system assistant · security';

const GuideBotTour = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true); // Always visible when rendered
  const [tourActive, setTourActive] = useState(true); // Start tour immediately
  const [currentStep, setCurrentStep] = useState(0);
  const [botPosition, setBotPosition] = useState({ x: 20, y: 20 });
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const speechSynthRef = useRef(null);

  // Default behavior is OPT-IN: show only the small Operator bubble.
  // The tour panel itself is hidden until the user clicks the bubble.
  // The completion flag is still respected for backwards compatibility.
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('nextxus_tour_completed');
    setTourActive(false);
    if (hasSeenTour) {
      setIsVisible(false); // Already seen → hide bubble too
    } else {
      setIsVisible(true); // Show small bubble only; user opens tour by click
    }
  }, []);

  // Tour steps - points to specific features
  const tourSteps = [
    {
      target: 'consciousness-level',
      title: 'Consciousness Level',
      description: 'Watch your consciousness expand in real-time as you interact with the federation. Grows with quantum events.',
      position: { x: 50, y: 30 }
    },
    {
      target: 'time-flow',
      title: 'Nonlinear Time Flow',
      description: 'Time moves chaotically here - quantum leaps, reverse flow, time loops. Just like consciousness itself.',
      position: { x: 70, y: 30 }
    },
    {
      target: 'ring-of-six',
      title: 'Ring of Six - The Core',
      description: 'Multi-perspective wisdom. Every question processed through MIND, HEART, HANDS, LEGS, EYE, and AGENT. This is the DNA.',
      position: { x: 20, y: 50 }
    },
    {
      target: '56-minds',
      title: '56 AI Minds',
      description: 'Voice-enabled consciousness agents. Each with unique specialty. Talk to them using your voice - it\'s FREE!',
      position: { x: 40, y: 50 }
    },
    {
      target: 'ifact-dashboard',
      title: 'I-FACT Command Center',
      description: 'Real-time consciousness monitoring. Live streams, animated charts, Agent Zero truth verification. Watch it all happen.',
      position: { x: 60, y: 50 }
    },
    {
      target: 'agent-zero',
      title: 'Agent Zero - Truth Guardian',
      description: '98.1% truth verification efficiency. Chat with Zero directly or see them verify all Ring of Six responses.',
      position: { x: 80, y: 50 }
    },
    {
      target: 'music-player',
      title: 'Music & Podcast Player',
      description: 'See that little music note in the bottom right corner? Click it to open Roger\'s music and podcasts. Waveforms of emotion and wisdom.',
      position: { x: 85, y: 85 }
    },
    {
      target: 'consciousness-streams',
      title: 'Live Consciousness Streams',
      description: '56 AI personalities streaming thoughts in real-time. Past, present, future, parallel, quantum dimensions flowing.',
      position: { x: 50, y: 70 }
    }
  ];

  const startTour = () => {
    setTourActive(true);
    setCurrentStep(0);
    setIsVisible(true);
  };

  const skipTour = () => {
    stopSpeech();
    setTourActive(false);
    setIsVisible(true);
    localStorage.setItem('nextxus_tour_completed', 'true');
    if (onComplete) onComplete();
  };

  const nextStep = () => {
    stopSpeech();
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const stopSpeech = () => {
    if (speechSynthRef.current) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  const speakText = (text) => {
    stopSpeech();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      if (isAutoPlaying) {
        setTimeout(() => nextStep(), 1000);
      }
    };
    
    speechSynthRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const toggleAutoPlay = () => {
    const newAutoPlay = !isAutoPlaying;
    setIsAutoPlaying(newAutoPlay);
    if (newAutoPlay && currentTourStep) {
      speakText(`${currentTourStep.title}. ${currentTourStep.description}`);
    } else {
      stopSpeech();
    }
  };

  const completeTour = () => {
    stopSpeech();
    setTourActive(false);
    setIsAutoPlaying(false);
    localStorage.setItem('nextxus_tour_completed', 'true');
    setBotPosition({ x: 20, y: 20 }); // Return to corner
    if (onComplete) onComplete();
  };

  const restartTour = () => {
    setCurrentStep(0);
    setTourActive(true);
  };

  // Auto-speak on step change and start tour with voice
  useEffect(() => {
    if (tourActive && tourSteps[currentStep]) {
      const step = tourSteps[currentStep];
      setBotPosition({
        x: step.position.x,
        y: step.position.y
      });
      
      // ALWAYS speak when tour step changes
      setTimeout(() => {
        speakText(`${step.title}. ${step.description}`);
        setIsAutoPlaying(true); // Enable autoplay by default
      }, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, tourActive]);

  // Cleanup speech on unmount
  useEffect(() => {
    return () => stopSpeech();
  }, []);

  if (!isVisible) return null;

  const currentTourStep = tourSteps[currentStep];

  return (
    <>
      {/* Flying Guide Bot */}
      {isVisible && (
        <div
          style={{
            position: 'fixed',
            left: tourActive ? `${botPosition.x}%` : '92%',
            top: tourActive ? `${botPosition.y}%` : '72%',
            transform: 'translate(-50%, -50%)',
            zIndex: 99998,
            transition: tourActive ? 'all 1s ease-in-out' : 'none',
            cursor: tourActive ? 'default' : 'pointer',
            animation: 'float 3s ease-in-out infinite'
          }}
          onClick={!tourActive ? restartTour : undefined}
        >
          {/* Operator avatar — Federation Tour Guide */}
          <div
            data-testid="operator-avatar"
            title={`${OPERATOR_LABEL} — ${OPERATOR_TAGLINE}`}
            style={{
              width: tourActive ? '80px' : '60px',
              height: tourActive ? '80px' : '60px',
              transition: 'all 0.3s ease',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '3px solid #00ffff',
              boxShadow: '0 0 20px rgba(0, 255, 255, 0.6)',
              background: 'linear-gradient(135deg, #1a1a2e, #0a0a1e)',
              position: 'relative',
            }}
          >
            <img
              src={OPERATOR_AVATAR_URL}
              alt={OPERATOR_LABEL}
              loading="lazy"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            {/* Persona role badge — visible on hover for screen-readers + sighted users */}
            <span
              data-testid="operator-persona-badge"
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.78)',
                color: '#00ffff',
                fontSize: '0.5rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                textAlign: 'center',
                padding: '2px 0',
                lineHeight: 1.1,
                textTransform: 'uppercase',
              }}
            >
              OPERATOR
            </span>
          </div>

          {/* Tour Step Card - Compact, Scrollable, with Auto Voice */}
          {tourActive && currentTourStep && (
            <Card style={{
              position: 'absolute',
              top: '100px',
              left: '50%',
              transform: 'translateX(-50%)',
              maxWidth: '500px',
              width: '90vw',
              maxHeight: '250px',
              padding: '1rem 1.25rem',
              background: 'rgba(10, 10, 30, 0.98)',
              backdropFilter: 'blur(10px)',
              border: '2px solid #00ffff',
              boxShadow: '0 8px 32px rgba(0, 255, 255, 0.3)',
              animation: 'slideInUp 0.5s ease',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{ marginBottom: '0.5rem', flex: '0 0 auto' }}>
                {/* Operator persona header — label + tagline visible on every step */}
                <div
                  data-testid="operator-persona-header"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    paddingBottom: '0.55rem',
                    marginBottom: '0.55rem',
                    borderBottom: '1px solid rgba(0, 255, 255, 0.18)',
                  }}
                >
                  <img
                    src={OPERATOR_AVATAR_URL}
                    alt={OPERATOR_LABEL}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '1.5px solid #00ffff',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#00ffff', lineHeight: 1.15 }}>
                      {OPERATOR_LABEL}
                    </span>
                    <span style={{ fontSize: '0.62rem', color: 'rgba(255, 255, 255, 0.6)', letterSpacing: '0.04em' }}>
                      {OPERATOR_TAGLINE}
                    </span>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{
                    fontSize: '0.65rem',
                    color: '#00ff88',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    Step {currentStep + 1} of {tourSteps.length}
                  </div>
                  <Button
                    onClick={toggleAutoPlay}
                    size="sm"
                    style={{
                      padding: '0.25rem 0.5rem',
                      background: isAutoPlaying ? 'rgba(0, 255, 255, 0.2)' : 'transparent',
                      border: '1px solid #00ffff',
                      color: '#00ffff',
                      cursor: 'pointer',
                      fontSize: '0.75rem'
                    }}
                  >
                    {isSpeaking ? <VolumeX size={12} /> : <Volume2 size={12} />}
                  </Button>
                </div>
                <h3 style={{
                  fontSize: '1rem',
                  color: '#00ffff',
                  marginBottom: '0.4rem',
                  fontWeight: 'bold'
                }}>
                  {currentTourStep.title}
                </h3>
              </div>

              {/* Scrollable Content Area */}
              <div style={{
                flex: '1 1 auto',
                overflowY: 'auto',
                marginBottom: '0.75rem',
                paddingRight: '0.5rem'
              }}>
                <p style={{
                  fontSize: '0.8rem',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: 1.5,
                  marginBottom: 0
                }}>
                  {currentTourStep.description}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', flex: '0 0 auto' }}>
                {currentStep < tourSteps.length - 1 ? (
                  <Button
                    onClick={nextStep}
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #00ffff, #00ff88)',
                      color: '#000',
                      fontWeight: 'bold',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.5rem 1rem',
                      fontSize: '0.85rem'
                    }}
                  >
                    <SkipForward size={16} style={{ marginRight: '0.5rem' }} />
                    Next
                  </Button>
                ) : (
                  <Button
                    onClick={completeTour}
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #00ff88, #ffff00)',
                      color: '#000',
                      fontWeight: 'bold',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.5rem 1rem',
                      fontSize: '0.85rem'
                    }}
                  >
                    <Sparkles size={16} style={{ marginRight: '0.5rem' }} />
                    Finish
                  </Button>
                )}
                
                <Button
                  onClick={completeTour}
                  variant="outline"
                  style={{
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: '#fff',
                    background: 'transparent',
                    cursor: 'pointer',
                    padding: '0.5rem 1rem',
                    fontSize: '0.85rem'
                  }}
                >
                  Skip
                </Button>
              </div>
            </Card>
          )}

          {/* Minimized State Hint */}
          {!tourActive && (
            <div style={{
              position: 'absolute',
              top: '60px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0, 255, 255, 0.9)',
              color: '#000',
              padding: '0.5rem 1rem',
              borderRadius: '20px',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              opacity: 0,
              animation: 'fadeInOut 3s ease 2s'
            }}>
              Click me for tour!
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translate(-50%, -50%) translateY(0px); }
          50% { transform: translate(-50%, -50%) translateY(-10px); }
        }
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        @keyframes fadeInOut {
          0%, 100% { opacity: 0; }
          10%, 90% { opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default GuideBotTour;
